import { SectionCard } from 'components/GridItemList';
import { SlidersHorizontalIcon } from 'components/icons';
import { FormattedDate } from 'components/misc/FormattedDate';
import { useModal } from 'components/modals/useModal';
import { EDIT_BUTTON_CLASS } from 'components/section-edit/SectionEdit';
import { Button } from 'components/ui/button';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { useInvoice } from 'pages/invoices/hooks/useInvoice';
import { INVOICE_LEARNING_KEYS } from 'pages/invoices/invoiceLearningHints';
import { MainPayerVatNote } from 'pages/invoices/payers/MainPayerVatNote';
import { formatShareRate } from 'pages/invoices/payers/paymentShare';
import { useSharedPayment } from 'pages/invoices/payers/useSharedPayment';
import {
    getBaseConversionDate,
    getInvoiceDeltaAmounts,
    getInvoiceTotalsAmounts,
} from 'pages/invoices/util/invoiceUtil';
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { useGetInvoiceTotalsQuery } from 'store/api/invoices';
import { FeatureFlag } from 'types/features';
import { InvoiceAmountDetailsTotalsDto, InvoiceData, InvoiceStatusType } from 'types/invoices';
import { cn } from 'utils/cn';
import { getClassifierText } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { useClassifiers } from 'utils/useClassifiers';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import RecalculatingOverlay from '../components/RecalculatingOverlay';
import { RoundingMatrixDialog } from '../views/totals-tab/RoundingMatrixAccordion';

// The receipt drops the card's radius, padding and shadow-card: `receipt-*` in index.css draw its edge. It holds the
// recalculating veil, which covers it whole, title included, over the last figures.
const RECEIPT_CLASS = 'receipt-tear receipt-paper new:shadow-none relative rounded-none rounded-t-[4px] pb-6';
const RULE_CLASS = 'border-border-control border-t border-dashed';
const ROW_CLASS = 'flex items-baseline justify-between gap-4 py-1';
const CAPTION_CLASS = 'text-caption text-foreground-tertiary tabular-nums';

const STATUS_CODES = ['INVOICE_STATUS_TYPE'];

// Typed as a Map, sent as a plain object of rate to amount.
const vatRateAmounts = (totals?: InvoiceAmountDetailsTotalsDto) =>
    Object.entries((totals?.vatRateAmounts ?? {}) as unknown as Record<string, number>);

interface Money {
    amount: number;
    currency?: string;
}

/** One figure of the receipt, its base-currency amount under it when the invoice is paid in another currency. */
function SummaryRow({
    label,
    value,
    base,
    className,
}: {
    label: ReactNode;
    value: Money;
    base?: Money;
    className?: string;
}) {
    return (
        <div className={cn(ROW_CLASS, className)}>
            <dt className="text-meta text-foreground-secondary">{label}</dt>
            <dd className="text-body1 m-0 flex flex-col items-end tabular-nums">
                <MoneyDisplay amount={value.amount} currency={value.currency} />
                {base ? (
                    <span className={CAPTION_CLASS}>
                        <MoneyDisplay amount={base.amount} currency={base.currency} />
                    </span>
                ) : null}
            </dd>
        </div>
    );
}

interface Props {
    invoice: InvoiceData;
    isInViewMode: boolean;
}

/**
 * The side column's Summary, drawn as a torn-off receipt: a correction's credit lines first, then the totals in
 * EN 16931's order (without VAT, VAT per rate, with VAT, prepayments) and the amount payable with the due date under
 * it. A shared-payment invoice adds this payer's share after the full totals and is payable by it. Rounding matrix
 * opens the matrix in a dialog.
 */
export function InvoiceSummaryCard({ invoice, isInViewMode }: Props) {
    const { data: invoiceTotals } = useGetInvoiceTotalsQuery(invoice.id);
    const { isPrepayment, isCorrection } = useInvoice(invoice);
    const { isSharedPaymentCopy, hasPaymentShare } = useSharedPayment(invoice);
    const hasRoundingMatrixPermission = useHasPermissions(['UPDATE.INVOICES.ROUNDING_MATRIX']);
    const isConvertBaseFromPaymentCurrency = useIsFeatureEnabled(
        FeatureFlag.INVOICING_CONVERT_BASE_CURRENCY_FROM_PAYMENT_CURRENCY
    );
    const { classifiers } = useClassifiers({ codes: STATUS_CODES });
    const { openModal, closeModal } = useModal();

    const payment = invoiceTotals?.paymentCurrency;
    const currency = payment?.currencyTypeCode;
    const baseCurrency = invoiceTotals?.baseCurrency.currencyTypeCode;
    const showBase = baseCurrency !== currency;
    const inBase = (amount: number): Money | undefined => (showBase ? { amount, currency: baseCurrency } : undefined);
    const {
        totalWithoutVatPaymentCurrency,
        totalWithVatPaymentCurrency,
        totalWithoutVatBaseCurrency,
        totalWithVatBaseCurrency,
    } = getInvoiceTotalsAmounts(invoiceTotals);
    const prepayment = payment?.invoice.prepaymentAmount ?? 0;
    const basePrepayment = invoiceTotals?.baseCurrency.invoice.prepaymentAmount ?? 0;
    const baseVat = (invoiceTotals?.baseCurrency.vatRateAmounts ?? {}) as unknown as Record<string, number>;
    // A credit note of a copy carries the copy's share and full amounts, negated, without being a copy itself.
    const hasShareTotals = isSharedPaymentCopy || (hasPaymentShare && !!invoice.preSplitTotals);
    const fullTotals = invoice.preSplitTotals?.paymentCurrency;
    const isPaid = invoice.invoiceStatusTypeCode === InvoiceStatusType.PAID;
    const payable = isPaid ? 0 : (payment?.invoice.totalWithVat ?? 0) - prepayment;
    const conversionDate = getBaseConversionDate(invoice, isConvertBaseFromPaymentCurrency);
    const delta = getInvoiceDeltaAmounts(invoice.deltaAmountsJson);
    const creditLines = [
        {
            id: 'invoices.totals-tab.delta-fees',
            amount: delta.deltaFeesPaymentCurrency,
            base: delta.deltaFeesBaseCurrency,
        },
        {
            id: 'invoices.totals-tab.delta-expenses',
            amount: delta.deltaExpensesPaymentCurrency,
            base: delta.deltaExpensesBaseCurrency,
        },
        {
            id: 'invoices.totals-tab.delta-subtotal-wo-vat',
            amount: delta.deltaTotalWithoutVatPaymentCurrency,
            base: delta.deltaTotalWithoutVatBaseCurrency,
        },
        {
            id: 'invoices.totals-tab.delta-vat',
            amount: delta.deltaVatPaymentCurrency,
            base: delta.deltaVatBaseCurrency,
        },
        {
            id: 'invoices.totals-tab.delta-subtotal-with-vat',
            amount: delta.deltaTotalWithVatPaymentCurrency,
            base: delta.deltaTotalWithVatBaseCurrency,
        },
    ].filter((line) => line.amount !== 0);

    const roundingMatrix =
        hasRoundingMatrixPermission && !hasPaymentShare ? (
            <Button
                variant="ghost"
                size="sm"
                className={EDIT_BUTTON_CLASS}
                onClick={() =>
                    openModal(
                        <RoundingMatrixDialog invoice={invoice} isInViewMode={isInViewMode} onClose={closeModal} />,
                        closeModal,
                        undefined,
                        undefined,
                        { panel: 'w-[640px]' }
                    )
                }
            >
                <SlidersHorizontalIcon data-icon="inline-start" className="size-3.5" />
                <FormattedMessage id="invoices.totals-tab.rounding-matrix.title" />
            </Button>
        ) : null;

    return (
        <div className="receipt-lift" data-learning={INVOICE_LEARNING_KEYS.summary}>
            <div className="[filter:var(--elev-receipt)]">
                <SectionCard
                    title={<FormattedMessage id="invoices.summary-sidebar.title" />}
                    action={roundingMatrix}
                    className={RECEIPT_CLASS}
                >
                    {isCorrection && creditLines.length > 0 ? (
                        <dl className={cn(RULE_CLASS, 'm-0 flex flex-col pt-2')}>
                            {creditLines.map((line, index) => (
                                <SummaryRow
                                    key={line.id}
                                    label={<FormattedMessage id={line.id} />}
                                    value={{ amount: line.amount, currency }}
                                    base={inBase(line.base)}
                                    className={index === creditLines.length - 1 ? 'font-medium' : undefined}
                                />
                            ))}
                        </dl>
                    ) : null}
                    <dl className={cn(RULE_CLASS, 'm-0 flex flex-col pt-2')}>
                        {hasShareTotals ? (
                            <>
                                <SummaryRow
                                    label={<FormattedMessage id="invoices.summary-sidebar.total-wo-vat" />}
                                    value={{ amount: fullTotals?.invoice.totalWithoutVat ?? 0, currency }}
                                />
                                {vatRateAmounts(fullTotals).map(([rate, amount]) => (
                                    <SummaryRow
                                        key={`full-vat-${rate}`}
                                        label={<FormattedMessage id="invoices.totals-tab.vat-rate" values={{ rate }} />}
                                        value={{ amount, currency }}
                                    />
                                ))}
                                <SummaryRow
                                    label={<FormattedMessage id="invoices.summary-sidebar.total-w-vat" />}
                                    value={{ amount: fullTotals?.invoice.totalWithVat ?? 0, currency }}
                                />
                                <SummaryRow
                                    label={<FormattedMessage id="invoices.summary-sidebar.prepayment" />}
                                    value={{ amount: prepayment * -1, currency }}
                                />
                                <div className={cn(ROW_CLASS, RULE_CLASS, 'mt-1.5 pt-2.5')}>
                                    <dt className="text-meta text-foreground font-medium">
                                        <FormattedMessage
                                            id="invoices.payers.totals.share-of"
                                            values={{
                                                payer: invoice.clientParty.name,
                                                rate: formatShareRate(invoice.paymentShareRate),
                                            }}
                                        />
                                    </dt>
                                    <dd className="m-0" />
                                </div>
                                <SummaryRow
                                    label={<FormattedMessage id="invoices.payers.totals.share-of-net" />}
                                    value={{ amount: payment?.invoice.totalWithoutVat ?? 0, currency }}
                                />
                                {vatRateAmounts(payment).map(([rate, amount]) => (
                                    <SummaryRow
                                        key={`share-vat-${rate}`}
                                        label={<FormattedMessage id="invoices.totals-tab.vat-rate" values={{ rate }} />}
                                        value={{ amount, currency }}
                                    />
                                ))}
                            </>
                        ) : (
                            <>
                                <SummaryRow
                                    label={<FormattedMessage id="invoices.summary-sidebar.total-wo-vat" />}
                                    value={{ amount: totalWithoutVatPaymentCurrency, currency }}
                                    base={inBase(totalWithoutVatBaseCurrency)}
                                />
                                {vatRateAmounts(payment).map(([rate, amount]) => (
                                    <SummaryRow
                                        key={`vat-${rate}`}
                                        label={<FormattedMessage id="invoices.totals-tab.vat-rate" values={{ rate }} />}
                                        value={{ amount, currency }}
                                        base={inBase(baseVat[rate] ?? 0)}
                                    />
                                ))}
                                <SummaryRow
                                    label={<FormattedMessage id="invoices.summary-sidebar.total-w-vat" />}
                                    value={{ amount: totalWithVatPaymentCurrency, currency }}
                                    base={inBase(totalWithVatBaseCurrency)}
                                />
                                {isPrepayment ? null : (
                                    <SummaryRow
                                        label={<FormattedMessage id="invoices.summary-sidebar.prepayment" />}
                                        value={{ amount: prepayment * -1, currency }}
                                        base={inBase(basePrepayment * -1)}
                                    />
                                )}
                            </>
                        )}
                        <div className={cn(ROW_CLASS, RULE_CLASS, 'mt-1.5 pt-3')}>
                            <dt className="text-body1 font-semibold">
                                <FormattedMessage
                                    id={
                                        hasShareTotals
                                            ? 'invoices.payers.totals.total-payable'
                                            : 'invoices.amount-payable'
                                    }
                                />
                            </dt>
                            <dd className="m-0 flex flex-col items-end">
                                <span className="text-figure tabular-nums">
                                    <MoneyDisplay amount={payable} currency={currency} />
                                </span>
                                {!hasShareTotals && !isPaid && showBase ? (
                                    <span className={CAPTION_CLASS}>
                                        <MoneyDisplay
                                            amount={totalWithVatBaseCurrency - basePrepayment}
                                            currency={baseCurrency}
                                        />
                                    </span>
                                ) : null}
                                {isPaid ? (
                                    <span className={CAPTION_CLASS}>
                                        {getClassifierText(classifiers['INVOICE_STATUS_TYPE'], InvoiceStatusType.PAID)}
                                    </span>
                                ) : invoice.dueDate ? (
                                    <span className={CAPTION_CLASS}>
                                        <FormattedMessage
                                            id="invoices.side-column.due"
                                            values={{ date: <FormattedDate value={invoice.dueDate} /> }}
                                        />
                                    </span>
                                ) : null}
                            </dd>
                        </div>
                    </dl>
                    {showBase && invoice.baseCurrencyPaymentCurrencyConversionRate ? (
                        <p className="text-caption text-foreground-tertiary m-0 tabular-nums">
                            <FormattedMessage id="invoices.totals-tab.exchange-rate" />
                            {` 1 ${currency} = ${Number(Number(invoice.baseCurrencyPaymentCurrencyConversionRate).toFixed(8))} ${baseCurrency}`}
                            {conversionDate ? (
                                <>
                                    {' · '}
                                    <FormattedDate value={conversionDate} />
                                </>
                            ) : null}
                        </p>
                    ) : null}
                    <MainPayerVatNote invoice={invoice} />
                    <RecalculatingOverlay invoiceId={invoice.id} className="bg-card/60" />
                </SectionCard>
            </div>
        </div>
    );
}
